import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { OnboardingAccount } from 'se-resource-types/dist/lib/Clare'
import { SeFeFormFieldConfig } from 'se-fe-form-field'
import { SeFeSelectOption } from 'se-fe-form-field-select'
import { SeFeThemesLoader } from 'se-fe-themes'
import { Subscription } from 'rxjs'

import { PayoutAccountService } from './payout-account.service'

interface PayoutAccountOption {
  id: string | number
  name?: string
  statement_descriptor?: string
  financial_contact_name?: string
  email?: string
  merchant_account_id?: number
  reference_id?: number
  is_default_processing?: boolean
  status?: string
  active_bank_account?: {
    name?: string
    last_four?: string
    account_type?: string
  }
}

export interface PayoutAccountSelection {
  id: string
  name: string
}

interface Accounts extends OnboardingAccount {
  accounts?: PayoutAccountOption[]
  multiple_accounts_enabled?: boolean
}

@Component({
  selector: 'payout-account-selector-element',
  templateUrl: './payout-account-selector.component.html',
  styleUrls: ['./payout-account-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PayoutAccountSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() organizationId: string
  @Input() accountId?: string
  @Input() host: 'invoice' | 'registration' | 'membership'
  @Output() payoutAccountChange = new EventEmitter<string | null>()
  @Output() payoutAccountSelected = new EventEmitter<PayoutAccountSelection | null>()
  @Output() multipleAccountsEnabledChange = new EventEmitter<boolean>()

  form: FormGroup
  payoutAccountFieldConfig: SeFeFormFieldConfig = {
    label: 'Payout account',
    required: true,
  }
  payoutAccountOptions: SeFeSelectOption[] = []
  multipleAccountsEnabled: boolean | null = null
  hasError = false
  private formSubscription: Subscription
  private accountsRequestSubscription: Subscription
  private loadedForOrganizationId: string | null = null
  private accountsResult: Accounts | null = null

  constructor(
    private fb: FormBuilder,
    private payoutAccountService: PayoutAccountService,
    private translate: TranslateService,
    private detector: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      payoutAccounts: [null as string | null, Validators.required],
    })

    translate.setDefaultLang('en')
    translate.use('en')

    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com',
    })
  }

  public ngOnInit(): void {
    this.formSubscription = this.form.get('payoutAccounts').valueChanges.subscribe((value) => {
      const selectedId = this.selectedAccountId(value)
      this.payoutAccountChange.emit(selectedId)
      this.payoutAccountSelected.emit(this.selectedAccount(selectedId))
    })

    this.loadAccountsIfReady()
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.organizationId || changes.accountId) {
      this.loadAccountsIfReady()
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe()
    this.accountsRequestSubscription?.unsubscribe()
  }

  private loadAccountsIfReady(): void {
    if (!this.organizationId) {
      return
    }

    if (this.accountsResult && this.loadedForOrganizationId === this.organizationId) {
      if (this.accountsResult.multiple_accounts_enabled) {
        this.preselectAccount(this.accountsResult)
        this.detector.detectChanges()
      }
      return
    }

    if (this.loadedForOrganizationId === this.organizationId) {
      return
    }
    this.loadedForOrganizationId = this.organizationId
    this.accountsResult = null

    this.accountsRequestSubscription?.unsubscribe()
    this.accountsRequestSubscription = this.payoutAccountService.getPayoutAccount(this.organizationId).subscribe({
      next: (response) => {
        this.hasError = false
        this.accountsResult = response.result as Accounts
        this.applyAccountsResponse(this.accountsResult)
      },
      error: () => {
        this.hasError = true
        this.accountsResult = null
        this.setMultipleAccountsEnabled(false)
      },
    })
  }

  private applyAccountsResponse(result: Accounts): void {
    const enabled = !!result?.multiple_accounts_enabled

    if (enabled) {
      this.payoutAccountOptions = this.buildPayoutAccountOptions(result)
      this.preselectAccount(result)
    }

    this.setMultipleAccountsEnabled(enabled)
  }

  private setMultipleAccountsEnabled(enabled: boolean): void {
    this.multipleAccountsEnabled = enabled
    this.multipleAccountsEnabledChange.emit(enabled)
    this.detector.detectChanges()
  }

  private buildPayoutAccountOptions(result: Accounts): SeFeSelectOption[] {
    const accounts = this.accountsFromResult(result)
    const completeAccounts = accounts.filter((account) => this.isCompleteAccount(account))

    return completeAccounts.map((account) => {
      const labels = [this.accountLabel(account)]

      if (account.is_default_processing) {
        labels[0] += ` ${this.translate.instant('PAYOUT_ACCOUNT_SELECTOR.primary')}`
      }

      const lastFour = account.active_bank_account?.last_four
      if (lastFour) {
        labels.push(
          this.translate.instant('PAYOUT_ACCOUNT_SELECTOR.account_ending_in', {
            lastFour,
          })
        )
      }

      return {
        label: labels.join(' — '),
        value: String(account.id ?? account.merchant_account_id ?? account.reference_id ?? ''),
      }
    })
  }

  private preselectAccount(result: Accounts): void {
    const completeAccounts = this.accountsFromResult(result).filter((account) => this.isCompleteAccount(account))

    const valueFromAccount = (account: PayoutAccountOption): string =>
      String(account.id ?? account.merchant_account_id ?? account.reference_id ?? '')

    const matchesAccountId = (account: PayoutAccountOption, accountId: string): boolean => {
      return [account.id, account.merchant_account_id, account.reference_id]
        .filter((candidate) => candidate != null && candidate !== '')
        .some((candidate) => String(candidate) === accountId)
    }

    let nextValue: string | null = null

    if (this.accountId != null && this.accountId !== '') {
      const wanted = String(this.accountId)
      const match = completeAccounts.find((account) => matchesAccountId(account, wanted))
      if (match) {
        const value = valueFromAccount(match)
        if (value) {
          nextValue = value
        }
      }
    } else if (completeAccounts.length === 1) {
      const value = valueFromAccount(completeAccounts[0])
      if (value) {
        nextValue = value
      }
    }

    const control = this.form.get('payoutAccounts')
    if (control && control.value !== nextValue) {
      control.setValue(nextValue)
    }
  }

  private accountsFromResult(result: Accounts): PayoutAccountOption[] {
    if (result?.accounts?.length) {
      return result.accounts
    }

    return result ? [result as unknown as PayoutAccountOption] : []
  }

  private isCompleteAccount(account: PayoutAccountOption): boolean {
    const status = account.status?.toLowerCase()
    return status === 'complete' || status === 'restricted_soon'
  }

  private accountLabel(account: PayoutAccountOption): string {
    return (
      account.name ||
      account.statement_descriptor ||
      account.financial_contact_name ||
      account.email ||
      String(
        account.id ??
          account.merchant_account_id ??
          account.reference_id ??
          this.translate.instant('PAYOUT_ACCOUNT_SELECTOR.fallback_label')
      )
    )
  }

  private selectedAccount(selectedId: string | null): PayoutAccountSelection | null {
    if (!selectedId) {
      return null
    }

    const account = this.accountsFromResult(this.accountsResult).find((candidate) =>
      [candidate.id, candidate.merchant_account_id, candidate.reference_id]
        .filter((value) => value != null && value !== '')
        .some((value) => String(value) === selectedId)
    )

    return {
      id: selectedId,
      name: account ? this.accountLabel(account) : selectedId,
    }
  }

  private selectedAccountId(value: unknown): string | null {
    const selected = Array.isArray(value) ? value[0] : value

    if (selected == null || selected === '') {
      return null
    }

    if (typeof selected === 'object' && selected !== null && 'value' in selected) {
      const optionValue = (selected as { value?: unknown }).value
      return optionValue == null || optionValue === '' ? null : String(optionValue)
    }

    return String(selected)
  }
}
